// src/components/SpectatorStatsGeneral.jsx
import React, { useState, useMemo } from 'react';
import { calculateAllStats } from '../utils/statsCalculator';
import StatsHelpModal from './StatsHelpModal';

// Composant StatRow (barres comparatives)
const StatRow = ({ stat }) => {
  const maxValue = stat.isCount ? Math.max(stat.j1Value, stat.j2Value, 10) : 100;
  const j1Width = (stat.j1Value / maxValue) * 100;
  const j2Width = (stat.j2Value / maxValue) * 100;

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: '20px',
        marginBottom: '4px'
      }}>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: '600',
          color: '#333',
          flex: 1,
          textAlign: 'left'
        }}>
          {stat.j1Total && `${stat.j1Value}% (${stat.j1Total})`}
          {!stat.j1Total && !stat.isCount && `${stat.j1Value}%`}
          {stat.isCount && stat.j1Value}
        </div>

        <div style={{ 
          fontSize: '12px', 
          fontWeight: '600',
          color: '#333',
          textAlign: 'center',
          minWidth: '180px',
          whiteSpace: 'nowrap'
        }}>
          {stat.name}
        </div>

        <div style={{ 
          fontSize: '11px', 
          fontWeight: '600',
          color: '#333',
          flex: 1,
          textAlign: 'right'
        }}>
          {stat.j2Total && `${stat.j2Value}% (${stat.j2Total})`}
          {!stat.j2Total && !stat.isCount && `${stat.j2Value}%`}
          {stat.isCount && stat.j2Value}
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        height: '24px',
        gap: 0
      }}>
        <div style={{ 
          flex: 1, 
          height: '20%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}>
          <div style={{ 
            height: '100%',
            width: `${j1Width}%`,
            backgroundColor: '#1976D2',
            borderRadius: '4px 0 0 4px',
            transition: 'width 0.3s ease'
          }} />
        </div>

        <div style={{ 
          width: '2px',
          height: '30%',
          backgroundColor: '#ddd'
        }} />

        <div style={{ 
          flex: 1, 
          height: '20%',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{ 
            height: '100%',
            width: `${j2Width}%`,
            backgroundColor: '#FF9800',
            borderRadius: '0 4px 4px 0',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    </div>
  );
};

// Fonction pour calculer les stats des zones de service
const buildZoneStats = (j1Zones, j2Zones, isFirst) => {
  const type = isFirst ? 'first' : 'second';
  const typeWon = isFirst ? 'firstWon' : 'secondWon';
  
  const calc = (won, total) => ({
    won: won || 0,
    total: total || 0,
    pct: total > 0 ? Math.round((won / total) * 100) : 0
  });
  
  const labels = {
    egalite: 'côté égalité',
    avantage: 'côté avantage',
    z1: 'zone 1 (corps)',
    z2: 'zone 2 (T)',
    z3: 'zone 3 (ext)',
    z4: 'zone 4 (ext)',
    z5: 'zone 5 (T)',
    z6: 'zone 6 (corps)'
  };
  
  return [
    ...['egalite', 'avantage'].map(z => {
      const j1 = calc(j1Zones[typeWon][z], j1Zones[type][z]);
      const j2 = calc(j2Zones[typeWon][z], j2Zones[type][z]);
      return {
        name: `% pts gagnés ${isFirst ? '1er' : '2nd'} serv ${labels[z]}`,
        j1Value: j1.pct,
        j2Value: j2.pct,
        j1Total: `${j1.won}/${j1.total}`,
        j2Total: `${j2.won}/${j2.total}`
      };
    }),
    ...['z1', 'z2', 'z3', 'z4', 'z5', 'z6'].map(z => {
      const j1 = calc(j1Zones[typeWon][z], j1Zones[type][z]);
      const j2 = calc(j2Zones[typeWon][z], j2Zones[type][z]);
      return {
        name: `% pts gagnés ${isFirst ? '1er' : '2nd'} serv ${labels[z]}`,
        j1Value: j1.pct,
        j2Value: j2.pct,
        j1Total: `${j1.won}/${j1.total}`,
        j2Total: `${j2.won}/${j2.total}`
      };
    })
  ];
};

// 🆕 Calcul stats par effet (P=Plat, S=Slicé, L=Lifté)
const buildEffetStats = (j1Stats, j2Stats, isFirst) => {
  const serveKey = isFirst ? 'first' : 'second';
  const label = isFirst ? '1er' : '2nd';
  const effets = [
    { code: 'P', label: 'Plat' },
    { code: 'S', label: 'Slicé' },
    { code: 'L', label: 'Lifté' }
  ];
  const calc = (won, total) => ({
    pct: total > 0 ? Math.round((won / total) * 100) : 0,
    won: won || 0,
    total: total || 0
  });
  const rows = [];
  effets.forEach(({ code, label: effLabel }) => {
    const j1e = j1Stats.effectStats?.[serveKey]?.[code] || {};
    const j2e = j2Stats.effectStats?.[serveKey]?.[code] || {};
    const j1g = calc(j1e.won, j1e.total);
    const j2g = calc(j2e.won, j2e.total);
    rows.push({ name: `% pts gagnés ${label} serv. ${effLabel}`, j1Value: j1g.pct, j2Value: j2g.pct, j1Total: `${j1g.won}/${j1g.total}`, j2Total: `${j2g.won}/${j2g.total}` });
    const j1eq = calc(j1e.egaliteWon, j1e.egalite);
    const j2eq = calc(j2e.egaliteWon, j2e.egalite);
    rows.push({ name: `% pts gagnés ${label} serv. égalité ${effLabel}`, j1Value: j1eq.pct, j2Value: j2eq.pct, j1Total: `${j1eq.won}/${j1eq.total}`, j2Total: `${j2eq.won}/${j2eq.total}` });
    const j1av = calc(j1e.avantageWon, j1e.avantage);
    const j2av = calc(j2e.avantageWon, j2e.avantage);
    rows.push({ name: `% pts gagnés ${label} serv. avantage ${effLabel}`, j1Value: j1av.pct, j2Value: j2av.pct, j1Total: `${j1av.won}/${j1av.total}`, j2Total: `${j2av.won}/${j2av.total}` });
  });
  return rows;
};

// 🆕 Calcul stats par effet ET zone
const buildEffetZoneStats = (j1Stats, j2Stats, isFirst) => {
  const serveKey = isFirst ? 'first' : 'second';
  const label = isFirst ? '1er' : '2nd';
  const effets = [{ code: 'P', label: 'Plat' }, { code: 'S', label: 'Slicé' }, { code: 'L', label: 'Lifté' }];
  const zones = [
    { code: 'z1', label: 'Ext. égalité' }, { code: 'z2', label: 'Corps égalité' }, { code: 'z3', label: 'T égalité' },
    { code: 'z4', label: 'T avantage' }, { code: 'z5', label: 'Corps avantage' }, { code: 'z6', label: 'Ext. avantage' }
  ];
  const calc = (won, total) => ({ pct: total > 0 ? Math.round((won / total) * 100) : 0, won: won || 0, total: total || 0 });
  const rows = [];
  effets.forEach(({ code, label: effLabel }) => {
    zones.forEach(({ code: zCode, label: zLabel }) => {
      const j1ez = j1Stats.effectZoneStats?.[serveKey]?.[code] || {};
      const j2ez = j2Stats.effectZoneStats?.[serveKey]?.[code] || {};
      const j1c = calc(j1ez[`${zCode}Won`], j1ez[zCode]);
      const j2c = calc(j2ez[`${zCode}Won`], j2ez[zCode]);
      rows.push({ name: `% pts gagnés ${label} serv. ${zLabel} ${effLabel}`, j1Value: j1c.pct, j2Value: j2c.pct, j1Total: `${j1c.won}/${j1c.total}`, j2Total: `${j2c.won}/${j2c.total}` });
    });
  });
  return rows;
};

// 🆕 Points sous pression
const buildPressureStats = (j1, j2) => [
  { name: '% balles de break sauvées', j1Value: j1.breakSavedPct, j2Value: j2.breakSavedPct, j1Total: `${j1.breakPointsSaved}/${j1.breakPointsFaced}`, j2Total: `${j2.breakPointsSaved}/${j2.breakPointsFaced}` },
  { name: '% balles de set sauvées', j1Value: j1.setPointsSavedPct, j2Value: j2.setPointsSavedPct, j1Total: `${j1.setPointsSaved}/${j1.setPointsFaced}`, j2Total: `${j2.setPointsSaved}/${j2.setPointsFaced}` },
  { name: '% balles de match sauvées', j1Value: j1.matchPointsSavedPct, j2Value: j2.matchPointsSavedPct, j1Total: `${j1.matchPointsSaved}/${j1.matchPointsFaced}`, j2Total: `${j2.matchPointsSaved}/${j2.matchPointsFaced}` },
  { name: '% pts gagnés à 40-40 (égalité)', j1Value: j1.equalityWonPct, j2Value: j2.equalityWonPct, j1Total: `${j1.equalityWon}/${j1.equalityPoints}`, j2Total: `${j2.equalityWon}/${j2.equalityPoints}` },
  { name: '% pts gagnés sur avantage (service)', j1Value: j1.advantageServingWonPct, j2Value: j2.advantageServingWonPct, j1Total: `${j1.advantageServingWon}/${j1.advantageServing}`, j2Total: `${j2.advantageServingWon}/${j2.advantageServing}` },
  { name: '% pts gagnés situation critique (serveur)', j1Value: j1.criticalServingWonPct, j2Value: j2.criticalServingWonPct, j1Total: `${j1.criticalServingWon}/${j1.criticalServing}`, j2Total: `${j2.criticalServingWon}/${j2.criticalServing}` },
  { name: '% pts gagnés situation critique (retourneur)', j1Value: j1.criticalReturningWonPct, j2Value: j2.criticalReturningWonPct, j1Total: `${j1.criticalReturningWon}/${j1.criticalReturning}`, j2Total: `${j2.criticalReturningWon}/${j2.criticalReturning}` },
  { name: '% jeux service gagnés (situation favorable)', j1Value: j1.favorableServeGamesWonPct, j2Value: j2.favorableServeGamesWonPct, j1Total: `${j1.favorableServeGamesWon}/${j1.favorableServeGames}`, j2Total: `${j2.favorableServeGamesWon}/${j2.favorableServeGames}` },
  { name: '% jeux retour gagnés (situation favorable)', j1Value: j1.favorableReturnGamesWonPct, j2Value: j2.favorableReturnGamesWonPct, j1Total: `${j1.favorableReturnGamesWon}/${j1.favorableReturnGames}`, j2Total: `${j2.favorableReturnGamesWon}/${j2.favorableReturnGames}` },
  { name: '% pts gagnés après 2 pts perdus', j1Value: j1.afterLossWonPct, j2Value: j2.afterLossWonPct, j1Total: `${j1.afterLossWon}/${j1.afterLossTotal}`, j2Total: `${j2.afterLossWon}/${j2.afterLossTotal}` },
  { name: '% pts gagnés après 2 pts gagnés', j1Value: j1.afterWinWonPct, j2Value: j2.afterWinWonPct, j1Total: `${j1.afterWinWon}/${j1.afterWinTotal}`, j2Total: `${j2.afterWinWon}/${j2.afterWinTotal}` },
  { name: 'Plus longue série de points perdus', j1Value: j1.longestLoseStreak, j2Value: j2.longestLoseStreak, isCount: true },
];

// Composant principal Stats Générales
function SpectatorStatsGeneral({ allPoints, playerNames }) {
  const [showZones1, setShowZones1] = useState(false);
  const [helpSection, setHelpSection] = useState(null);
  const [showZones2, setShowZones2] = useState(false);
  const [showFautes, setShowFautes] = useState(false);
  const [showPressure, setShowPressure] = useState(false);
  const [showEffets1, setShowEffets1] = useState(false);
  const [showEffets2, setShowEffets2] = useState(false);
  const [showEffetsZones1, setShowEffetsZones1] = useState(false);
  const [showEffetsZones2, setShowEffetsZones2] = useState(false);

  // 🆕 Mémoriser le calcul des stats pour ne pas recalculer à chaque render
  const { statsJ1, statsJ2, totalPoints } = useMemo(() => {
    if (!allPoints || allPoints.length === 0) {
      return { statsJ1: null, statsJ2: null, totalPoints: 0 };
    }
    
    return {
      statsJ1: calculateAllStats(allPoints, 'J1'),
      statsJ2: calculateAllStats(allPoints, 'J2'),
      totalPoints: allPoints.length
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPoints?.length]); // 🆕 Ne recalculer que si le NOMBRE de points change

  if (!allPoints || allPoints.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '20px', 
        color: '#999',
        fontSize: '13px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        Aucun point disponible
      </div>
    );
  }

  if (!statsJ1 || !statsJ2) {
    return null;
  }

  // Construire les données
  const stats = {
    service: [
      { name: 'Aces & Services gagnants', j1Value: statsJ1.aces, j2Value: statsJ2.aces, isCount: true },
      { name: 'Double fautes', j1Value: statsJ1.dfs, j2Value: statsJ2.dfs, isCount: true },
      { 
        name: 'Balles de break sauvées', 
        j1Value: statsJ1.breakSavedPct, 
        j2Value: statsJ2.breakSavedPct,
        j1Total: `${statsJ1.breakPointsSaved}/${statsJ1.breakPointsFaced}`,
        j2Total: `${statsJ2.breakPointsSaved}/${statsJ2.breakPointsFaced}`
      },
      { 
        name: '% 1er service', 
        j1Value: statsJ1.firstPct, 
        j2Value: statsJ2.firstPct,
        j1Total: `${statsJ1.firstServes}/${statsJ1.totalServeAttempts}`,
        j2Total: `${statsJ2.firstServes}/${statsJ2.totalServeAttempts}`
      },
      { 
        name: '% Points gagnés 1er service', 
        j1Value: statsJ1.firstWonPct, 
        j2Value: statsJ2.firstWonPct,
        j1Total: `${statsJ1.firstWon}/${statsJ1.firstServes}`,
        j2Total: `${statsJ2.firstWon}/${statsJ2.firstServes}`
      },
      { 
        name: '% Points gagnés 2nd service', 
        j1Value: statsJ1.secondWonPct, 
        j2Value: statsJ2.secondWonPct,
        j1Total: `${statsJ1.secondWon}/${statsJ1.secondServes}`,
        j2Total: `${statsJ2.secondWon}/${statsJ2.secondServes}`
      }
    ],
    zoneServe1: buildZoneStats(statsJ1.zoneStats, statsJ2.zoneStats, true),
    zoneServe2: buildZoneStats(statsJ1.zoneStats, statsJ2.zoneStats, false),
    effets1: buildEffetStats(statsJ1, statsJ2, true),
    effets2: buildEffetStats(statsJ1, statsJ2, false),
    effetsZones1: buildEffetZoneStats(statsJ1, statsJ2, true),
    effetsZones2: buildEffetZoneStats(statsJ1, statsJ2, false),
    return: [
      { 
        name: 'Balles de break converties', 
        j1Value: statsJ1.breakConvertedPct, 
        j2Value: statsJ2.breakConvertedPct,
        j1Total: `${statsJ1.breakPointsConverted}/${statsJ1.breakPointsAgainst}`,
        j2Total: `${statsJ2.breakPointsConverted}/${statsJ2.breakPointsAgainst}`
      },
      { 
        name: '% points gagnés retour 1er service', 
        j1Value: statsJ1.returnFirstPct, 
        j2Value: statsJ2.returnFirstPct,
        j1Total: `${statsJ1.returnFirstWon}/${statsJ1.returnFirst}`,
        j2Total: `${statsJ2.returnFirstWon}/${statsJ2.returnFirst}`
      },
      { 
        name: '% points gagnés retour 2nd service', 
        j1Value: statsJ1.returnSecondPct, 
        j2Value: statsJ2.returnSecondPct,
        j1Total: `${statsJ1.returnSecondWon}/${statsJ1.returnSecond}`,
        j2Total: `${statsJ2.returnSecondWon}/${statsJ2.returnSecond}`
      }
    ],
    points: [
      { 
        name: 'Total points gagnés', 
        j1Value: Math.round((statsJ1.totalPoints / totalPoints) * 100), 
        j2Value: Math.round((statsJ2.totalPoints / totalPoints) * 100),
        j1Total: `${statsJ1.totalPoints}/${totalPoints}`,
        j2Total: `${statsJ2.totalPoints}/${totalPoints}`
      },
      { name: 'Total nb points gagnants', j1Value: statsJ1.winners, j2Value: statsJ2.winners, isCount: true },
      { 
        name: '% points gagnés au service',
        j1Value: statsJ1.serviceWonPct, j2Value: statsJ2.serviceWonPct,
        j1Total: `${statsJ1.totalServeWon}/${statsJ1.totalServePoints}`,
        j2Total: `${statsJ2.totalServeWon}/${statsJ2.totalServePoints}`
      },
      { 
        name: '% points gagnés au retour',
        j1Value: statsJ1.returnWonPct, j2Value: statsJ2.returnWonPct,
        j1Total: `${statsJ1.totalReturnWon}/${statsJ1.totalReturnPoints}`,
        j2Total: `${statsJ2.totalReturnWon}/${statsJ2.totalReturnPoints}`
      },
      { 
        name: '% points gagnés au filet',
        j1Value: statsJ1.volleyWonPct, j2Value: statsJ2.volleyWonPct,
        j1Total: `${statsJ1.volleyWon}/${statsJ1.volleyTotal}`,
        j2Total: `${statsJ2.volleyWon}/${statsJ2.volleyTotal}`
      },
      { name: 'Total nb fautes directes', j1Value: statsJ1.unForcedErrors, j2Value: statsJ2.unForcedErrors, isCount: true },
    ],
    pressure: buildPressureStats(statsJ1, statsJ2),
    fautesDetails: [
      { name: 'Nb fautes directes filet', j1Value: statsJ1.netErrors, j2Value: statsJ2.netErrors, isCount: true },
      { name: 'Nb fautes directes couloir', j1Value: statsJ1.wideErrors, j2Value: statsJ2.wideErrors, isCount: true },
      { name: 'Nb fautes directes longueur', j1Value: statsJ1.longErrors, j2Value: statsJ2.longErrors, isCount: true }
    ],
    jeux: [
      { name: 'Total de jeux gagnés', j1Value: statsJ1.gamesWon, j2Value: statsJ2.gamesWon, isCount: true },
      { name: 'Jeux de services gagnés', j1Value: statsJ1.gamesServing, j2Value: statsJ2.gamesServing, isCount: true },
      { name: 'Jeux de retours gagnés', j1Value: statsJ1.gamesReturning, j2Value: statsJ2.gamesReturning, isCount: true }
    ]
  };

  return (
    <>
    <div>
      {/* Header joueurs */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 16px',
        marginBottom: '16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '2px',
            backgroundColor: '#1976D2'
          }} />
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
            {playerNames.joueur1}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
            {playerNames.joueur2}
          </span>
          <div style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '2px',
            backgroundColor: '#FF9800'
          }} />
        </div>
      </div>

      {/* Section Service */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
            Service
          </h3>
          <button onClick={() => setHelpSection('SERVICE')} style={{ width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#1976D2', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>?</button>
        </div>
        {stats.service.map((stat, i) => (
          <StatRow key={i} stat={stat} />
        ))}

        {/* Boutons zones service */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px'
        }}>
          <button
            onClick={() => setShowZones1(!showZones1)}
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: showZones1 ? '#e3f2fd' : 'white',
              color: '#333',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px' }}>{showZones1 ? '−' : '+'}</span>
            <span>Zones 1er service</span>
          </button>
          <button
            onClick={() => setShowZones2(!showZones2)}
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: showZones2 ? '#e3f2fd' : 'white',
              color: '#333',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px' }}>{showZones2 ? '−' : '+'}</span>
            <span>Zones 2nd service</span>
          </button>
        </div>

        {/* Stats zones 1er service */}
        {showZones1 && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            {stats.zoneServe1.map((stat, i) => (
              <StatRow key={`z1-${i}`} stat={stat} />
            ))}
          </div>
        )}

        {/* Stats zones 2nd service */}
        {showZones2 && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            {stats.zoneServe2.map((stat, i) => (
              <StatRow key={`z2-${i}`} stat={stat} />
            ))}
          </div>
        )}

        {/* 🆕 Boutons effets */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button onClick={() => setShowEffets1(!showEffets1)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: showEffets1 ? '#e3f2fd' : 'white', color: '#333', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>{showEffets1 ? '−' : '+'}</span>
            <span>Effets 1er service</span>
          </button>
          <button onClick={() => setShowEffets2(!showEffets2)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: showEffets2 ? '#e3f2fd' : 'white', color: '#333', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>{showEffets2 ? '−' : '+'}</span>
            <span>Effets 2nd service</span>
          </button>
        </div>

        {showEffets1 && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            {stats.effets1.map((stat, i) => <StatRow key={`eff1-${i}`} stat={stat} />)}
          </div>
        )}
        {showEffets2 && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            {stats.effets2.map((stat, i) => <StatRow key={`eff2-${i}`} stat={stat} />)}
          </div>
        )}

        {/* 🆕 Boutons effets & zones */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button onClick={() => setShowEffetsZones1(!showEffetsZones1)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: showEffetsZones1 ? '#e3f2fd' : 'white', color: '#333', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>{showEffetsZones1 ? '−' : '+'}</span>
            <span>Effets & zones 1er serv</span>
          </button>
          <button onClick={() => setShowEffetsZones2(!showEffetsZones2)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: showEffetsZones2 ? '#e3f2fd' : 'white', color: '#333', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>{showEffetsZones2 ? '−' : '+'}</span>
            <span>Effets & zones 2nd serv</span>
          </button>
        </div>

        {showEffetsZones1 && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            {stats.effetsZones1.map((stat, i) => <StatRow key={`effz1-${i}`} stat={stat} />)}
          </div>
        )}
        {showEffetsZones2 && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            {stats.effetsZones2.map((stat, i) => <StatRow key={`effz2-${i}`} stat={stat} />)}
          </div>
        )}
      </div>

      {/* Section Retour */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
            Retour
          </h3>
          <button onClick={() => setHelpSection('RETOUR')} style={{ width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#FF9800', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>?</button>
        </div>
        {stats.return.map((stat, i) => (
          <StatRow key={i} stat={stat} />
        ))}
      </div>

      {/* Section Points */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
            Points
          </h3>
          <button onClick={() => setHelpSection('POINTS')} style={{ width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#388E3C', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>?</button>
        </div>
        {stats.points.map((stat, i) => (
          <StatRow key={i} stat={stat} />
        ))}

        {/* Bouton détail fautes */}
        <button
          onClick={() => setShowFautes(!showFautes)}
          style={{
            width: '100%',
            padding: '10px',
            marginTop: '12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            backgroundColor: showFautes ? '#e3f2fd' : 'white',
            color: '#333',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>{showFautes ? '−' : '+'}</span>
          <span>Détails fautes directes</span>
        </button>

        {/* Détails fautes directes */}
        {showFautes && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            {stats.fautesDetails.map((stat, i) => (
              <StatRow key={`faute-${i}`} stat={stat} />
            ))}
          </div>
        )}

        {/* 🆕 Bouton Points sous pression */}
        <button
          onClick={() => setShowPressure(!showPressure)}
          style={{
            width: '100%',
            padding: '10px',
            marginTop: '12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            backgroundColor: showPressure ? '#e3f2fd' : 'white',
            color: '#333',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>{showPressure ? '−' : '+'}</span>
          <span>Points sous pression</span>
        </button>

        {showPressure && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            {stats.pressure.map((stat, i) => <StatRow key={`pp-${i}`} stat={stat} />)}
          </div>
        )}
      </div>

      {/* Section Jeux */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
            Jeux
          </h3>
          <button onClick={() => setHelpSection('JEUX')} style={{ width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#7B1FA2', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>?</button>
        </div>
        {stats.jeux.map((stat, i) => (
          <StatRow key={i} stat={stat} />
        ))}
      </div>
    </div>
      {helpSection && <StatsHelpModal section={helpSection} onClose={() => setHelpSection(null)} />}
    </>
  );
}

export default SpectatorStatsGeneral;